import {
  HttpRequest,
  HttpResponse,
  HttpInterceptorFn,
  HttpHandlerFn,
  HttpEvent,
  HttpHeaders,
} from "@angular/common/http";
import { Observable, of, tap } from "rxjs";
import { environment } from "@environments/environment";
import { isPublicCacheRequest } from "./api-request-policy";

interface CachedPublicResponse {
  response: HttpResponse<unknown>;
  expiresAt: number;
}

function forbidsCaching(headers: HttpHeaders): boolean {
  const directives = (headers.getAll("Cache-Control") ?? [])
    .join(",")
    .toLowerCase()
    .split(",");
  return directives.some((value) =>
    /^(?:no-cache|no-store|private)(?:\s*=|$)/.test(value.trim()),
  );
}

/** Only explicitly public liveness responses may enter the application cache. */
class HttpCacheService {
  private readonly cache = new Map<string, CachedPublicResponse>();
  private generation = 0;
  private readonly maxEntries = 100;

  get epoch(): number {
    return this.generation;
  }

  get(url: string): HttpResponse<unknown> | null {
    const entry = this.cache.get(url);
    if (!entry) {
      return null;
    }
    if (Date.now() >= entry.expiresAt) {
      this.cache.delete(url);
      return null;
    }
    return entry.response.clone();
  }

  set(url: string, response: HttpResponse<unknown>, epoch: number): void {
    // A pre-invalidation request must never repopulate even this public cache.
    if (epoch !== this.generation || forbidsCaching(response.headers)) {
      return;
    }
    const control = (response.headers.getAll("Cache-Control") ?? [])
      .join(",")
      .toLowerCase();
    if (!control.split(",").some((value) => value.trim() === "public")) {
      return;
    }
    // Cache keys do not model request-header variants.
    if (
      (response.headers.getAll("Vary") ?? []).some(
        (value) => value.trim().length > 0,
      )
    ) {
      return;
    }
    const ages = control
      .split(",")
      .filter((value) => /^\s*max-age(?:\s|=|$)/.test(value));
    const age =
      ages.length === 1 ? /^\s*max-age\s*=\s*(\d+)\s*$/.exec(ages[0]) : null;
    if (ages.length > 1 || (ages.length === 1 && !age)) {
      return;
    }
    const seconds = age ? Number(age[1]) : 5;
    if (!Number.isSafeInteger(seconds) || seconds <= 0) {
      return;
    }
    const ttl = Math.min(5000, seconds * 1000);
    if (this.cache.size >= this.maxEntries) {
      const oldest = this.cache.keys().next().value;
      if (oldest !== undefined) {
        this.cache.delete(oldest);
      }
    }
    this.cache.set(url, {
      response: response.clone(),
      expiresAt: Date.now() + ttl,
    });
  }

  clear(): void {
    this.generation += 1;
    this.cache.clear();
  }

  clearExpired(): void {
    for (const [url, entry] of this.cache) {
      if (Date.now() >= entry.expiresAt) {
        this.cache.delete(url);
      }
    }
  }

  invalidate(pattern: string | RegExp): void {
    this.generation += 1;
    for (const url of this.cache.keys()) {
      // String invalidations are literal URLs, not executable regular expressions.
      const matches =
        typeof pattern === "string" ? url.includes(pattern) : pattern.test(url);
      if (matches) {
        this.cache.delete(url);
      }
    }
  }
}

const cacheService = new HttpCacheService();

/**
 * Cache only explicitly public GET liveness responses. All protected API data,
 * including permissions and Copilot status/history, always reaches the server.
 * This runs before auth headers are attached, so absence of Authorization alone
 * is never evidence that an endpoint is public.
 */
export const cacheInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  if (!["GET", "HEAD", "OPTIONS"].includes(req.method)) {
    cacheService.clear();
  }
  if (
    !environment.cache.enableCaching ||
    !isPublicCacheRequest(req.method, req.url) ||
    forbidsCaching(req.headers) ||
    ["Authorization", "X-Tenant-Id", "Cookie"].some((name) =>
      req.headers.has(name),
    )
  ) {
    return next(req);
  }

  const key = req.urlWithParams;
  const cached = cacheService.get(key);
  if (cached) {
    return of(cached);
  }
  const epoch = cacheService.epoch;
  return next(req).pipe(
    tap((event) => {
      if (
        event instanceof HttpResponse &&
        event.status >= 200 &&
        event.status < 300
      ) {
        cacheService.set(key, event, epoch);
      }
    }),
  );
};

/** Retained public invalidation interface for existing feature callers. */
export const cacheManager = {
  clearAll(): void {
    cacheService.clear();
  },
  clearExpired(): void {
    cacheService.clearExpired();
  },
  invalidate(pattern: string | RegExp): void {
    cacheService.invalidate(pattern);
  },
  invalidateResource(
    resourceType: "symbols" | "config" | "users" | "dashboard" | "reports",
  ): void {
    cacheService.invalidate(`/api/v1/${resourceType}`);
  },
};
